// src/components/ShopifyConnectCard.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { AppleComboBox } from "../components/AppleComboBox";
import { apiGet, apiPost } from "../api/client";
import { A, pillClass } from "../styles/appleClasses";
import { Button } from "./Button";
import ProBadge from "./ProBadge";
import { companyRoute } from "../utils/routeHelpers";
import ShopifyBillingRequiredPanel from "./shopify/ShopifyBillingRequiredPanel";
import { isShopifyBillingRequired, type ShopifyStatus } from "../types/shopify";

type Account = {
  id: number;
  name: string;
  type?: string | null;
  kind?: string | null;
  is_system?: boolean;
};

type ShopifyPayoutSettings = {
  default_bank_account_id?: number | null;
};

type SyncError = {
  id: number;
  scope: string;
  shopify_object_type?: string | null;
  shopify_object_id?: string | null;
  message: string;
  status: string;
  created_at?: string | null;
  resolved_at?: string | null;
};

type OrderLink = {
  id: number;
  shopify_order_id: string;
  shopify_order_number?: string | null;
  sale_id?: number | null;
  invoice_id?: number | null;
  import_status: string;
  imported_at?: string | null;
};

type ShopifyConnectCardProps = {
  isPro: boolean;
  planLoading?: boolean;
  onUpgradeClick: () => void;
};

function shortDate(value?: string | null) {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function errorMessage(e: any, fallback: string) {
  return e?.response?.data?.detail || e?.message || fallback;
}

function latestOrderLabel(order: OrderLink | null) {
  if (!order) return "—";

  const orderLabel = order.shopify_order_number || order.shopify_order_id;
  const importedAt = shortDate(order.imported_at);

  if (!orderLabel) return importedAt;
  if (importedAt === "—") return orderLabel;

  return `${orderLabel} · ${importedAt}`;
}

function SummaryLinkRow({
  to,
  label,
  value,
  helper,
}: {
  to: string;
  label: string;
  value: React.ReactNode;
  helper?: string;
}) {
  return (
    <Link
      to={to}
      className="
        group flex items-center justify-between gap-4
        -mx-1 px-4 py-3
        rounded-xl
        transition-colors hover:bg-gray-50
      "
    >
      <div>
        <div className="text-[13px] font-medium text-gray-800">{label}</div>
        {helper ? <div className={`${A.label} mt-0.5`}>{helper}</div> : null}
      </div>

      <div className="flex items-center gap-2 text-right text-[13px] font-medium text-gray-700 group-hover:text-gray-900">
        <span>{value}</span>
      </div>
    </Link>
  );
}

export default function ShopifyConnectCard({
  isPro,
  planLoading = false,
  onUpgradeClick,
}: ShopifyConnectCardProps) {
  const { company_id } = useParams<{ company_id: string }>();

  const ordersUrl = companyRoute(company_id, "/commerce/shopify/orders");
  const issuesUrl = companyRoute(company_id, "/commerce/shopify/sync-issues");

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [shopInput, setShopInput] = useState("");

  const [status, setStatus] = useState<ShopifyStatus | null>(null);
  const [errors, setErrors] = useState<SyncError[]>([]);
  const [orders, setOrders] = useState<OrderLink[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [payoutBankAccountId, setPayoutBankAccountId] = useState("");
  const [savingPayoutBank, setSavingPayoutBank] = useState(false);

  const connected = !!status?.connected;
  const pricingRequired = !!status?.pricing_required;
  const canUseShopify = !!status?.can_use_shopify;
  const requiresReconnect = !!status?.requires_reconnect;
  const shopifyBillingRequired = isShopifyBillingRequired(status);

  const payoutBankAccounts = useMemo(
    () =>
      accounts.filter((account) => {
        const type = (account.type || "").toLowerCase();
        const kind = (account.kind || "").toLowerCase();
        return type === "asset" && kind === "bank" && !account.is_system;
      }),
    [accounts],
  );

  const selectedPayoutBankAccount =
    payoutBankAccounts.find((a) => String(a.id) === payoutBankAccountId) ||
    null;

  const openErrors = useMemo(
    () => errors.filter((issue) => issue.status === "open"),
    [errors],
  );

  const latestOrder = orders[0] || null;

  const showShopifyProUpsell = false;

  const shouldShowShopifyPricingCard =
    !connected &&
    !loading &&
    !planLoading &&
    status?.shopify_connector_active === false;

  async function refresh() {
    setBusy(true);
    setErr(null);

    try {
      const statusRes = await apiGet<ShopifyStatus>("/shopify/status");
      setStatus(statusRes);

      const accountRows = await apiGet<Account[]>(
        company_id ? `/accounts?company_id=${company_id}` : "/accounts",
      );
      setAccounts(accountRows || []);

      if (isShopifyBillingRequired(statusRes)) {
        setErrors([]);
        setOrders([]);
        setPayoutBankAccountId("");
        if (statusRes?.shop_domain) {
          setShopInput(statusRes.shop_domain);
        }
        return;
      }

      const [errorRes, orderRes, payoutSettings] = await Promise.all([
        apiGet<SyncError[]>("/shopify/sync-errors"),
        apiGet<OrderLink[]>("/shopify/order-links"),
        apiGet<ShopifyPayoutSettings>("/shopify/payout-settings").catch(() => ({
          default_bank_account_id: null,
        })),
      ]);

      setErrors(errorRes || []);
      setOrders(orderRes || []);

      const defaultBankAccountId =
        payoutSettings?.default_bank_account_id ??
        statusRes?.default_payout_bank_account_id ??
        null;
      setPayoutBankAccountId(
        defaultBankAccountId ? String(defaultBankAccountId) : "",
      );

      if (statusRes?.shop_domain) {
        setShopInput(statusRes.shop_domain);
      }
    } catch (e: any) {
      setErr(errorMessage(e, "Failed to load Shopify status."));
    } finally {
      setLoading(false);
      setBusy(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function startConnect() {
    const shop = (connected ? status?.shop_domain || "" : shopInput).trim();

    if (!shop) {
      setErr("Enter your Shopify .myshopify.com shop domain to connect.");
      return;
    }

    setConnecting(true);
    setErr(null);

    try {
      const res = await apiGet<{ url: string }>(
        `/shopify/connect/start?shop=${encodeURIComponent(shop)}`,
      );

      if (!res?.url) {
        throw new Error("Shopify connect URL was not returned.");
      }

      window.location.assign(res.url);
    } catch (e: any) {
      setErr(errorMessage(e, "Could not start Shopify connect."));
      setConnecting(false);
    }
  }

  async function disconnectShopify() {
    if (!connected) return;

    const ok = window.confirm(
      "Disconnect this Shopify store from Nimos? Existing imported records remain in Nimos.",
    );

    if (!ok) return;

    setDisconnecting(true);
    setErr(null);

    try {
      await apiPost("/shopify/disconnect", {});
      await refresh();
    } catch (e: any) {
      setErr(errorMessage(e, "Could not disconnect Shopify."));
    } finally {
      setDisconnecting(false);
    }
  }

  async function savePayoutBankAccount(nextValue: string) {
    setPayoutBankAccountId(nextValue);

    const nextId = nextValue ? Number(nextValue) : null;
    if (nextValue && !Number.isFinite(nextId)) {
      setErr("Select a valid Shopify payout bank account.");
      return;
    }

    setSavingPayoutBank(true);
    setErr(null);
    setNotice(null);

    try {
      await apiPost("/shopify/payout-settings", {
        default_bank_account_id: nextId,
      });
      await refresh();
    } catch (e: any) {
      setErr(errorMessage(e, "Could not save Shopify payout bank account."));
    } finally {
      setSavingPayoutBank(false);
    }
  }

  return (
    <div className={`${A.card} p-4 sm:p-5`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {!isPro && !planLoading ? <ProBadge size="xs" /> : null}

          <span
            className={pillClass(
              requiresReconnect
                ? "warning"
                : pricingRequired
                  ? "warning"
                  : connected
                    ? "success"
                    : "neutral",
            )}
          >
            {requiresReconnect
              ? "Reconnect Required"
              : pricingRequired
                ? "Plan Pending"
                : connected
                  ? "Connected"
                  : "Not connected"}
          </span>

          <button
            type="button"
            onClick={refresh}
            disabled={busy || connecting || disconnecting}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-appleblue transition hover:bg-gray-100 hover:text-appleblueD focus:outline-none focus:ring-2 focus:ring-appleblueD disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Refresh Shopify status"
            title={busy ? "Refreshing…" : "Refresh"}
          >
            <ArrowPathIcon
              className={`h-5 w-5 ${busy ? "animate-spin" : ""}`}
              aria-hidden="true"
            />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {requiresReconnect ? (
            <Button
              type="button"
              variant="primary"
              onClick={startConnect}
              disabled={connecting || busy || disconnecting}
              minWidth="auto"
              className="px-4"
            >
              {connecting ? "Opening Shopify…" : "Reconnect"}
            </Button>
          ) : !connected ? (
            <Button
              type="button"
              variant="primary"
              onClick={startConnect}
              disabled={connecting || busy || !shopInput.trim()}
              minWidth="auto"
              className="px-4"
            >
              {connecting
                ? "Opening Shopify…"
                : "Connect Shopify"}
            </Button>
          ) : (
            <button
              type="button"
              onClick={disconnectShopify}
              disabled={disconnecting || busy || connecting}
              className="inline-flex h-9 items-center justify-center rounded-xl border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-500 transition hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {disconnecting ? "Disconnecting…" : "Disconnect"}
            </button>
          )}
        </div>
      </div>

      {err ? (
        <div className="mt-3 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
          {err}
        </div>
      ) : null}

      {notice ? (
        <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
          {notice}
        </div>
      ) : null}

      {requiresReconnect ? (
        <div className="mt-3 rounded-xl border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Shopify needs to be reconnected before Nimos can sync orders,
          inventory, or payouts.
        </div>
      ) : null}

      {showShopifyProUpsell && !isPro && !planLoading ? (
        <div className="mt-4 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className={A.title}>
                Shopify Commerce is available on Pro
              </div>
              <div className={`${A.label} mt-1`}>
                Upgrade to connect or reconnect a Shopify store.
              </div>
            </div>

            <Button
              type="button"
              variant="primary"
              onClick={onUpgradeClick}
              minWidth="auto"
              className="px-4"
            >
              Upgrade
            </Button>
          </div>
        </div>
      ) : null}

      {shopifyBillingRequired && status ? (
        <div className="mt-4">
          <ShopifyBillingRequiredPanel
            installedAppUrl={status.installed_app_url}
            shopifyAdminAppUrl={status.shopify_admin_app_url}
          />
        </div>
      ) : null}

      {shouldShowShopifyPricingCard ? (
        <div className="mt-4 rounded-2xl bg-gray-50 px-4 py-4 ring-1 ring-gray-200">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="text-[15px] font-semibold text-gray-700">
                {isPro ? "Shopify Connector" : "Nimos Pro + Shopify Connector"}
              </div>
              <div className="mt-1 text-[13px] text-gray-500">
                {isPro ? "Connector add-on" : "Includes Nimos Pro and Shopify Connector"}
              </div>
            </div>

            <div className="sm:text-right">
              <div className="text-[15px] font-semibold tabular-nums text-gray-900">
                {isPro ? "US$2/month" : "US$9/month"}
              </div>
              <div className="mt-1 text-[13px] text-gray-500">
                Billed by Shopify
              </div>
            </div>
          </div>

          <div className="mt-3 border-t border-gray-200 pt-3 text-[13px] leading-5 text-gray-600">
            <p>Charges appear on your Shopify invoice.</p>
            <p>
              Shopify will show the final plan and billing approval before
              activation.
            </p>
          </div>
        </div>
      ) : null}

      <div className="mt-4 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className={A.label}>Connected store</div>

            {connected ? (
              <div className="mt-1 truncate text-[13px] text-gray-700">
                {loading ? "Loading…" : status?.shop_domain || "—"}
              </div>
            ) : (
              <input
                id="shopify-shop-domain"
                value={shopInput}
                onChange={(e) => setShopInput(e.target.value)}
                disabled={connecting || busy}
                placeholder="mystore.myshopify.com"
                className="mt-2 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-[13px] text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-appleblue focus:ring-2 focus:ring-appleblue/20 disabled:bg-gray-50 disabled:text-gray-500"
              />
            )}
          </div>
        </div>

        {connected ? (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className={A.label}>Shopify Payout to this Account</div>
              </div>

              <div className="w-full sm:min-w-[220px] sm:max-w-[260px]">
                <AppleComboBox
                  className="w-full"
                  options={payoutBankAccounts}
                  value={selectedPayoutBankAccount}
                  onChange={(opt) => {
                    if (!opt) return;
                    savePayoutBankAccount(String(opt.id));
                  }}
                  getOptionLabel={(opt) => opt.name}
                  placeholder={
                    payoutBankAccounts.length === 0
                      ? "No bank accounts found"
                      : "Select bank account"
                  }
                  disabled={
                    savingPayoutBank ||
                    busy ||
                    shopifyBillingRequired ||
                    !canUseShopify ||
                    payoutBankAccounts.length === 0
                  }
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {!shopifyBillingRequired ? (
        <div className="mt-4 overflow-hidden rounded-2xl border border-gray-100 bg-white">
          <SummaryLinkRow
            to={issuesUrl}
            label="Open Issues"
            value={loading ? "—" : openErrors.length}
            helper="Review issues."
          />

          <div className="mx-4 h-px bg-gray-100" />

          <SummaryLinkRow
            to={ordersUrl}
            label="Last Order Import"
            value={loading ? "—" : latestOrderLabel(latestOrder)}
            helper="View orders."
          />
        </div>
      ) : null}
    </div>
  );
}
